import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";

interface ConnectivityState {
  online: boolean;
  syncPending: number;
  lastSyncAt: string | null;
  syncInProgress: boolean;
}

interface ConnectivityContextValue extends ConnectivityState {
  triggerSync: () => void;
}

const ConnectivityContext = createContext<ConnectivityContextValue>({
  online: true,
  syncPending: 0,
  lastSyncAt: null,
  syncInProgress: false,
  triggerSync: () => {},
});

export function ConnectivityProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConnectivityState>({
    online: navigator.onLine,
    syncPending: 0,
    lastSyncAt: localStorage.getItem("sparrow-last-sync"),
    syncInProgress: false,
  });

  // Browser online/offline events
  useEffect(() => {
    const handleOnline = () => setState((s) => ({ ...s, online: true }));
    const handleOffline = () => setState((s) => ({ ...s, online: false }));
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Electron IPC for tray-based sync trigger
  useEffect(() => {
    const api = (window as any).electronAPI;
    if (!api?.onTriggerSync) return;
    return api.onTriggerSync(() => triggerSync());
  }, []);

  // Periodic connectivity check (every 30s)
  useEffect(() => {
    const check = async () => {
      try {
        const api = (window as any).electronAPI;
        if (api?.getConnectivityStatus) {
          const result = await api.getConnectivityStatus();
          setState((s) => ({ ...s, online: result.online }));
        }
      } catch {
        // Keep current state
      }
    };
    const interval = setInterval(check, 30_000);
    check();
    return () => clearInterval(interval);
  }, []);

  const triggerSync = useCallback(async () => {
    if (state.syncInProgress || !state.online) return;
    setState((s) => ({ ...s, syncInProgress: true }));
    try {
      const res = await fetch("/api/sync/push", { method: "POST", credentials: "include" });
      if (res.ok) {
        const now = new Date().toISOString();
        localStorage.setItem("sparrow-last-sync", now);
        setState((s) => ({ ...s, syncInProgress: false, lastSyncAt: now, syncPending: 0 }));
      }
    } catch {
      setState((s) => ({ ...s, syncInProgress: false }));
    }
  }, [state.syncInProgress, state.online]);

  return (
    <ConnectivityContext.Provider value={{ ...state, triggerSync }}>
      {children}
    </ConnectivityContext.Provider>
  );
}

export const useConnectivity = () => useContext(ConnectivityContext);
